import {
  Breadcrumb,
  BreadcrumbLink,
  BreadcrumbPage,
  Button,
  Pagination,
} from '@ads/components-react';
import { ClipboardDocumentListIcon } from '@heroicons/react/24/outline';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { BotExamVisualizationQuestionViewModel } from '@/shared/api/generated/types/BotExamVisualizationQuestionViewModel';
import type { ClassroomDisciplineBotExamVisualizationViewModel } from '@/shared/api/generated/types/ClassroomDisciplineBotExamVisualizationViewModel';
import { Skeleton } from '@/shared/ui/atoms';
import { AlternativeList, QuestionCardHeader, QuestionSection } from '@/shared/ui/molecules';
import { decodeHtmlEntities } from '@/shared/utils/decodeHtmlEntities';

const PT_BR = 'pt-BR';

/** Mapeia o código de cultura para a chave i18n do rótulo da aba de idioma. */
const LANG_LABEL_KEYS: Record<string, 'langPt' | 'langEn' | 'langEs'> = {
  'pt-BR': 'langPt',
  'en-US': 'langEn',
  'es-ES': 'langEs',
};

/** Chaves i18n do rótulo de tipo da questão (enum EBotExamType). */
const TYPE_KEYS: Record<
  number,
  'default' | 'trueFalse' | 'multipleAffirmations' | 'fillInTheBlanks'
> = {
  1: 'default',
  2: 'trueFalse',
  3: 'multipleAffirmations',
  4: 'fillInTheBlanks',
};

/**
 * Props do template de visualização da prova gerada da revisão.
 */
export interface RevisionVisualizeTemplateProps {
  /** Dados gerais da prova (título e instrução). */
  readonly exam: ClassroomDisciplineBotExamVisualizationViewModel | null;
  /** Questão atualmente exibida (pt-BR, com suas traduções). */
  readonly question: BotExamVisualizationQuestionViewModel | null;
  /** Página de questão atual (1-based). */
  readonly currentPage: number;
  /** Total de questões (páginas). */
  readonly totalPages: number;
  /** Navega para outra questão (1-based). */
  readonly onPageChange: (page: number) => void;
  /** Estado de carregamento. */
  readonly isLoading: boolean;
  /** Indica falha na consulta. */
  readonly isError: boolean;
  /** Refaz a consulta em caso de erro. */
  readonly onRetry: () => void;
}

/**
 * Template de visualização somente-leitura de uma prova gerada por IA no fluxo de
 * revisão. Reaproveita os molecules compartilhados de preview (`QuestionCardHeader`,
 * `QuestionSection`, `AlternativeList`) e consome os dados vindos do endpoint
 * `/exam/revision/{id}/visualize` — sem depender do fluxo de exam-management.
 *
 * Exibe rótulo, trecho da aula, explicação e alternativas da questão, com abas de
 * idioma quando a questão possui traduções.
 */
export function RevisionVisualizeTemplate({
  exam,
  question,
  currentPage,
  totalPages,
  onPageChange,
  isLoading,
  isError,
  onRetry,
}: RevisionVisualizeTemplateProps) {
  const { t } = useTranslation('examRevision');
  const [selectedCulture, setSelectedCulture] = useState(PT_BR);

  const translations = question?.translations ?? [];
  const cultures = [PT_BR, ...translations.map(tr => tr.cultureInfo ?? '').filter(Boolean)];
  const effectiveCulture = cultures.includes(selectedCulture) ? selectedCulture : PT_BR;

  // pt-BR e as traduções compartilham a mesma forma (label/transcript/explanation/options).
  const display =
    effectiveCulture === PT_BR
      ? question
      : (translations.find(tr => tr.cultureInfo === effectiveCulture) ?? question);

  const options = (display?.options ?? []).map(opt => ({
    value: decodeHtmlEntities(opt.label ?? ''),
    checked: opt.isCorrect ?? false,
  }));
  const hasOptions = options.length > 0;
  const transcriptExcerpt = decodeHtmlEntities(display?.transcriptExcerpt ?? '');
  const explanation = decodeHtmlEntities(display?.explanation ?? '');

  const typeKey = question?.type !== undefined ? TYPE_KEYS[question.type] : undefined;
  const typeLabel = typeKey ? t(`visualize.type.${typeKey}`) : null;

  const showTabs = !isLoading && !isError && question !== null && translations.length > 0;

  return (
    <div className="flex flex-col gap-sm">
      {/* Page header: breadcrumb + título agrupados com gap menor */}
      <div className="flex flex-col gap-nano">
        <Breadcrumb>
          <BreadcrumbLink>
            <Link to="/">{t('breadcrumb.home')}</Link>
          </BreadcrumbLink>
          <BreadcrumbLink>
            <Link to={'/exam/revision' as never}>{t('breadcrumb.revision')}</Link>
          </BreadcrumbLink>
          <BreadcrumbPage>{t('visualize.breadcrumb')}</BreadcrumbPage>
        </Breadcrumb>

        {/* Title + instruction */}
        <div className="flex flex-col gap-quark">
          <span className="ts-heading-lg text-ctx-content-title">
            {exam?.title ?? t('visualize.title')}
          </span>
          {exam?.instruction && (
            <p className="whitespace-pre-line ts-paragraph-xs text-ctx-content-placeholder">
              {decodeHtmlEntities(exam.instruction)}
            </p>
          )}
        </div>
      </div>

      {/* Question card */}
      <div className="overflow-hidden rounded-md border-hairline border-ctx-layout-border bg-ctx-layout-surface shadow-level1">
        <QuestionCardHeader
          label={
            totalPages > 0 ? t('visualize.question', { number: currentPage }) : t('visualize.title')
          }
          tagLabel={typeLabel}
          icon={ClipboardDocumentListIcon}
        />

        {/* Language tabs */}
        {showTabs && (
          <div className="flex items-center gap-xs border-b-hairline border-ctx-layout-border px-xs py-quark">
            {cultures.map(culture => (
              <button
                key={culture}
                type="button"
                onClick={() => setSelectedCulture(culture)}
                className={[
                  'ts-paragraph-xs pb-quark transition-colors',
                  culture === effectiveCulture
                    ? 'border-b-thin border-ctx-content-title !font-semibold text-ctx-content-title'
                    : 'text-ctx-content-base hover:text-ctx-content-title',
                ].join(' ')}
              >
                {LANG_LABEL_KEYS[culture] ? t(`visualize.${LANG_LABEL_KEYS[culture]}`) : culture}
              </button>
            ))}
          </div>
        )}

        <div className="flex flex-col gap-mini p-xs">
          {isLoading ? (
            <div className="flex flex-col gap-quark">
              <Skeleton className="h-mini w-full" />
              <Skeleton className="h-mini w-full" />
              <Skeleton className="h-mini w-3/4" />
            </div>
          ) : isError ? (
            <div className="flex flex-col items-center justify-center gap-nano py-xl">
              <span className="ts-paragraph-xs text-ctx-content-placeholder">
                {t('visualize.error')}
              </span>
              <Button hierarchy="secondary" size="sm" onClick={onRetry}>
                {t('visualize.retry')}
              </Button>
            </div>
          ) : !question ? (
            <div className="flex items-center justify-center py-xl">
              <span className="ts-paragraph-xs text-ctx-content-placeholder">
                {t('visualize.noQuestions')}
              </span>
            </div>
          ) : (
            <>
              {/* Rótulo */}
              <QuestionSection label={t('visualize.label')}>
                <p className="whitespace-pre-line ts-paragraph-xs text-ctx-content-base">
                  {decodeHtmlEntities(display?.label ?? '')}
                </p>
              </QuestionSection>

              {/* Trecho da aula */}
              {transcriptExcerpt && (
                <QuestionSection label={t('visualize.transcriptExcerpt')}>
                  <p className="whitespace-pre-line ts-paragraph-xs text-ctx-content-base">
                    {transcriptExcerpt}
                  </p>
                </QuestionSection>
              )}

              {/* Explicação */}
              {explanation && (
                <QuestionSection label={t('visualize.explanation')}>
                  <p className="whitespace-pre-line ts-paragraph-xs text-ctx-content-base">
                    {explanation}
                  </p>
                </QuestionSection>
              )}

              {/* Alternativas */}
              <div className="flex flex-col gap-nano">
                <span className="ts-heading-xxs text-ctx-content-title">
                  {t('visualize.alternatives')}
                </span>
                {hasOptions ? (
                  <AlternativeList options={options} />
                ) : (
                  <p className="italic ts-caption-xxs text-ctx-content-placeholder">
                    {t('visualize.openAnswer')}
                  </p>
                )}
              </div>
            </>
          )}
        </div>

        {totalPages > 0 && (
          <div className="flex justify-center px-xs pb-xs">
            <Pagination
              pageAmount={totalPages}
              activeIndex={currentPage - 1}
              setActiveIndex={v =>
                onPageChange((typeof v === 'function' ? v(currentPage - 1) : v) + 1)
              }
              ellipsisLabel="…"
            />
          </div>
        )}
      </div>
    </div>
  );
}
